import animation, { cubicBezier } from "@constants/animation"
import colors from "@constants/colors"
import {
  body14Styles,
  smallcapsBold13Styles,
  smallcapsRegular13Styles,
} from "@constants/typography"
import { getShopURLForBrandDeviceType, getShopURLForDevice } from "@utils/urls"
import { Link } from "gatsby"
import _ from "lodash-es"
import React from "react"
import styled from "styled-components"

const Container = styled.div`
  margin-bottom: 72px;
  padding-right: 32px;

  &:nth-child(3) {
    padding-right: 0;
  }
`

const Brand = styled(Link)`
  ${smallcapsBold13Styles}
  display: flex;
  height: 40px;
  margin-bottom: 10px;
  padding: 4px 12px 0;
  align-items: center;
  transition: background-color ${animation.medium} ${cubicBezier};
  background-color: transparent;

  &:hover {
    background-color: ${colors.dividerLineGray};
    transition: background-color ${animation.quick} ${cubicBezier};
  }
`

const Devices = styled.div`
  display: flex;
  flex-direction: column;
`

const Device = styled(Link)`
  ${body14Styles}
  display: inline-flex;
  padding: 0 12px;
  align-items: flex-start;
  color: ${colors.mediumDarkGray};
  line-height: 32px;
  transition: background-color ${animation.medium} ${cubicBezier};
  background-color: transparent;

  &:hover {
    transition: background-color ${animation.quick} ${cubicBezier};
    background-color: ${colors.dividerLineGray};
  }
`

const MoreDevicesText = styled.span`
  ${body14Styles}
  height: 32px;
  color: ${colors.offBlack};
  transition:
    color ${animation.medium} ${cubicBezier},
    border ${animation.medium} ${cubicBezier};
  border-bottom: solid 1px ${colors.lightGray};
`

const MoreDevices = styled.button<{ $hideButton: boolean }>`
  display: flex;
  width: auto;
  margin: 16px 0 0;
  padding: 0 0 0 12px;
  background: none;
  border: none;

  ${({ $hideButton }) =>
    $hideButton &&
    `
      width: 0;
      height: 0;
      margin: 0;
      visibility: hidden;
  `}

  &:active {
    outline: none;
  }

  &:focus {
    outline: none;
  }

  &:hover {
    cursor: pointer;

    ${MoreDevicesText} {
      color: ${colors.black};
      transition:
        color ${animation.quick} ${cubicBezier},
        border ${animation.quick} ${cubicBezier};
      border-bottom: solid 1px ${colors.black};
    }
  }
`

const Tag = styled.div`
  ${smallcapsRegular13Styles}
  display: flex;
  width: 32px;
  height: 16px;
  margin: 6px 0 0 10px;
  padding-top: 3px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: ${colors.offWhite};
  line-height: 13px;
  letter-spacing: 0.5px;
  background-color: ${colors.brandOrange};
  border-radius: 2px;
`

interface DeviceListProps {
  brandName: string
  brandSlug: string
  devices: Array<{
    name: string
    new?: boolean
    slug: string
  }>
  devicesToShow?: number
  deviceTypeSlug: string
}

const DeviceList: React.FC<DeviceListProps> = ({
  brandName,
  brandSlug,
  devices,
  devicesToShow,
  deviceTypeSlug,
}) => {
  const uniqueDevices = _.uniqBy(devices, "slug")
  const [showMoreDevices, setShowMoreDevices] = React.useState<boolean>(false)

  const handleShowMoreDevices = () => {
    setShowMoreDevices(true)
  }

  const renderedDevices =
    devicesToShow && !showMoreDevices
      ? uniqueDevices.slice(0, devicesToShow)
      : uniqueDevices

  return (
    <Container>
      <Devices>
        <Brand to={getShopURLForBrandDeviceType(brandSlug, deviceTypeSlug)}>
          {brandName}
        </Brand>
        {renderedDevices.map((device) => (
          <Device
            key={`${deviceTypeSlug}-${device.slug}`}
            to={getShopURLForDevice(device.slug)}
          >
            {device.name}
            {device.new && <Tag>New</Tag>}
          </Device>
        ))}
        {devicesToShow && (
          <MoreDevices
            $hideButton={renderedDevices.length === uniqueDevices.length}
            onClick={handleShowMoreDevices}
          >
            <MoreDevicesText>{`View ${
              uniqueDevices.length - devicesToShow
            } More`}</MoreDevicesText>
          </MoreDevices>
        )}
      </Devices>
    </Container>
  )
}

export default DeviceList
